<template>

  <report-panel icon="$bookingDuration" :title="$t('titles.booking_duration')" :loading="loading">

    <div ref="chartContainer">
      <bar
        :chart-options="chartOptions"
        :chart-data="chartData"
        :styles="chartStyles"
      />

    </div>

  </report-panel>

</template>

<script setup lang="ts">

import { computed, ref, useTemplateRef } from 'vue'
import { computedAsync } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import ReportPanel from './ReportPanel.vue'
import { type GetBookingDurationResponse, ReportingApi } from '../../../../services/api-clients/ReportingApi'
import NotificationService from '../../../../services/NotificationService'
import useFitChartToContainer from '../../../../utils/useFitChartToContainer'

// ChartJS
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  type ChartOptions,
  type ChartData, type ChartDataset, type TooltipItem
} from 'chart.js'
import { Temporal } from '@js-temporal/polyfill'

ChartJS.register(Tooltip, Legend, BarElement, CategoryScale, LinearScale)

interface Props {
  locationIds: number[]
  channels: string[]
  start: null | Temporal.PlainDate
  end: null | Temporal.PlainDate
}

const props = withDefaults(defineProps<Props>(), {
  channels: () => [],
})

const { t } = useI18n()

/**
 * Chart
 */

const chartOptions: ChartOptions<'bar'> = {
  plugins: {
    tooltip: {
      callbacks: {
        title: (tooltipItems: TooltipItem<'bar'>[]): string => {
          return `${tooltipItems[0].label} – ${tooltipItems[0].dataset.label}`
        },
        label: (context: TooltipItem<'bar'>): string => {
          return `${Math.round((context.raw as number) * 100) / 100}%`
        },
      },
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      type: 'linear',
      min: 0,
      max: 100,
      ticks: {
        callback: (value, _index, _values) => {
          return `${value}%`
        },
      },
    },
  },
}

/**
 * Data retrieval
 */

const loading = ref(false)
const pendingRequestAbortControllers: AbortController[] = []

const rawData = computedAsync<null | GetBookingDurationResponse>(

  async () => {

    if (
      props.locationIds.length === 0 ||
      !props.start ||
      !props.end
    ) {
      return null
    }

    pendingRequestAbortControllers.forEach((controller) => {
      controller.abort()
    })
    pendingRequestAbortControllers.length = 0

    const abortController = new AbortController()
    pendingRequestAbortControllers.push(abortController)

    loading.value = true
    let data = null

    await ReportingApi.getBookingDuration({
      locationIds: props.locationIds,
      start: props.start,
      end: props.end,
      signal: abortController.signal,
    })
    .then((response) => {
      data = response
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t load booking duration data!')
    })
    .finally(() => {
      loading.value = false
    })

    return data

  },

  null // default value

)

const chartData = computed<ChartData<'bar', number[]>>(() => {

  const datasets: ChartDataset<'bar'>[] = []

  const weekdays: {[key: string]: {label: string, numTransactions: number}} = {
    mon: {label: t('enums.weekdays_abbr.mon'), numTransactions: 0},
    tue: {label: t('enums.weekdays_abbr.tue'), numTransactions: 0},
    wed: {label: t('enums.weekdays_abbr.wed'), numTransactions: 0},
    thu: {label: t('enums.weekdays_abbr.thu'), numTransactions: 0},
    fri: {label: t('enums.weekdays_abbr.fri'), numTransactions: 0},
    sat: {label: t('enums.weekdays_abbr.sat'), numTransactions: 0},
    sun: {label: t('enums.weekdays_abbr.sun'), numTransactions: 0},
  }

  if (rawData.value) {

    // scaffold the totals object
    const totals: {[key: string]: {[key: string]: number}} = {}
    Object.keys(rawData.value.segments).forEach(segmentKey => {
      totals[segmentKey] = {mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0}
    })

    // add up totals
    Object.entries(rawData.value.by_weekday).forEach(([weekdayKey, weekdayItem]) => {
      Object.entries(weekdayItem.by_channel)
        .filter(([channel, _channelItem]) => {
          return props.channels.length === 0 || props.channels.includes(channel)
        })
        .forEach(([_channel, channelItem]) => {
          Object.entries(channelItem).forEach(([segmentKey, numTransactions]) => {
            weekdays[weekdayKey].numTransactions += numTransactions
            totals[segmentKey][weekdayKey] += numTransactions
          })
        })
    })

    // derive the final datasets

    const colors = ['#003f5c', '#374c80', '#7a5195', '#bc5090', '#ef5675', '#ff764a', '#ffa600']

    Object.entries(rawData.value.segments).forEach(([segmentKey, segment], i) => {

      const data = Object.entries(weekdays).map(([weekdayKey, weekday]) => {
        let percentage = 0
        if (weekday.numTransactions) {
          percentage = totals[segmentKey][weekdayKey] / weekday.numTransactions * 100
        }
        return percentage
      })

      datasets.push({
        label: `${segment.from}${segment.to === null ? '+ hr' : ' – ' + segment.to + ' hr'}`, // TODO: i18n
        type: 'bar',
        data: data,
        backgroundColor: colors[i],
      })

    })

  }

  return {
    labels: Object.values(weekdays).map((weekday) => weekday.label),
    datasets: datasets,
  }

})

/**
 * Adjust the chart size responsively
 * https://vue-chartjs.org/guide/#chart-with-dynamic-styles
 */

const container = useTemplateRef('chartContainer') // template ref
const { chartStyles } = useFitChartToContainer(container)

</script>
